import {
  ApiError,
  formatMoney,
  queryKeys,
  type RepairLabourEntry,
  type RepairLabourResponse,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Clock, Plus, Receipt, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface StaffRow {
  id: string
  name: string
  isActive: boolean
}

/** 90 → "1.5 h", 45 → "0.75 h". */
function hoursLabel(minutes: number): string {
  return `${Number((minutes / 60).toFixed(2))} h`
}

/**
 * t1365: hourly labour on one repair. Technicians log time at an hourly rate
 * (defaulting from Settings); "Charge labour" rings the unbilled total up at
 * the till under the labour item chosen in Settings, so it is taxed like any
 * other sale and no second repair ticket is opened.
 */
export function RepairLabourCard({
  ticketId,
  ticketNumber,
  customerId,
  assignedStaffId,
  cancelled,
}: {
  ticketId: string
  ticketNumber: string
  customerId: string
  assignedStaffId: string | null
  cancelled: boolean
}) {
  const { can } = useAuth()
  const canWrite = can('repairs:write') && !cancelled
  const [adding, setAdding] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.repairs.labour(ticketId),
    queryFn: () => api.get<RepairLabourResponse>(`/repairs/${ticketId}/labour`),
  })
  const entries = data?.items ?? []

  const remove = useMutation({
    mutationFn: (entry: RepairLabourEntry) => api.delete(`/repairs/${ticketId}/labour/${entry.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.repairs.labour(ticketId) })
      toast.success('Time removed')
    },
    onError: (error: Error) =>
      toast.error('Could not remove it', error instanceof ApiError ? error.message : undefined),
  })

  const unbilledCents = data?.unbilledCents ?? 0
  const canCharge =
    can('orders:write') && !cancelled && unbilledCents > 0 && data?.labourProduct != null

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Clock className="size-4" aria-hidden="true" /> Labour
        </CardTitle>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" aria-hidden="true" /> Log time
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-12" />
        ) : entries.length === 0 ? (
          <p className="text-sm text-foreground-muted">No time logged yet.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {hoursLabel(entry.minutes)} × {formatMoney(entry.rateCents)}/h
                  </p>
                  <p className="text-xs text-foreground-muted">
                    {[
                      entry.staffName,
                      entry.workDate ??
                        new Date(entry.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        }),
                      entry.note,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {entry.billedOrderId ? (
                    <Badge tone="success">
                      <Link to="/orders/$orderId" params={{ orderId: entry.billedOrderId }}>
                        Charged · {entry.billedOrderNumber}
                      </Link>
                    </Badge>
                  ) : null}
                  <span className="tabular">{formatMoney(entry.amountCents)}</span>
                  {canWrite && !entry.billedOrderId ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Remove this time"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(entry)}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
            <li className="flex justify-between py-2 font-medium">
              <span>Not yet charged · {hoursLabel(data?.unbilledMinutes ?? 0)}</span>
              <span className="tabular">{formatMoney(unbilledCents)}</span>
            </li>
          </ul>
        )}

        {unbilledCents > 0 && data && data.labourProduct == null ? (
          <p className="mt-3 text-xs text-foreground-muted">
            To charge this at the till, choose a labour item in{' '}
            <Link to="/settings" className="underline">
              Settings
            </Link>
            .
          </p>
        ) : null}
        {canCharge && data?.labourProduct ? (
          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-foreground-muted">
              Sold as {data.labourProduct.name}
              {data.labourProduct.taxCategoryName
                ? ` · taxed as ${data.labourProduct.taxCategoryName}`
                : ' · not taxed'}
            </p>
            <Button size="sm" variant="primary" asChild>
              <Link
                to="/orders/new"
                search={{
                  customerId,
                  addProductId: data.labourProduct.id,
                  addPriceCents: unbilledCents,
                  repairTicketId: ticketId,
                  repairTicketNumber: ticketNumber,
                }}
              >
                <Receipt className="size-3.5" aria-hidden="true" /> Charge labour
              </Link>
            </Button>
          </div>
        ) : null}
      </CardContent>
      <LogTimeDialog
        ticketId={ticketId}
        assignedStaffId={assignedStaffId}
        defaultRateCents={data?.defaultRateCents ?? 0}
        open={adding}
        onClose={() => setAdding(false)}
      />
    </Card>
  )
}

function LogTimeDialog({
  ticketId,
  assignedStaffId,
  defaultRateCents,
  open,
  onClose,
}: {
  ticketId: string
  assignedStaffId: string | null
  defaultRateCents: number
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { can } = useAuth()
  const [hours, setHours] = useState('')
  const [rate, setRate] = useState<string | null>(null)
  const [staffId, setStaffId] = useState<string | null>(null)
  const [workDate, setWorkDate] = useState(() => new Date().toLocaleDateString('en-CA'))
  const [note, setNote] = useState('')

  const { data: staff } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffRow[]>('/staff'),
    enabled: open && can('staff:read'),
  })
  const activeStaff = (staff ?? []).filter((s) => s.isActive)

  const shownRate = rate ?? (defaultRateCents / 100).toFixed(2)
  const hoursNum = Number(hours)
  const rateNum = Number(shownRate)
  const minutes = Math.round(hoursNum * 60)
  const valid =
    hours.trim() !== '' &&
    Number.isFinite(hoursNum) &&
    minutes >= 1 &&
    minutes <= 10000 &&
    shownRate.trim() !== '' &&
    Number.isFinite(rateNum) &&
    rateNum >= 0
  const previewCents = valid ? Math.round((minutes * Math.round(rateNum * 100)) / 60) : 0

  function close() {
    setHours('')
    setRate(null)
    setStaffId(null)
    setNote('')
    onClose()
  }

  const add = useMutation({
    mutationFn: () =>
      api.post(`/repairs/${ticketId}/labour`, {
        minutes,
        rateCents: Math.round(rateNum * 100),
        staffId: staffId ?? assignedStaffId ?? null,
        workDate: workDate || null,
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.repairs.labour(ticketId) })
      toast.success('Time logged')
      close()
    },
    onError: (error: Error) =>
      toast.error('Could not log the time', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Log time</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hours">
              {(props) => (
                <Input
                  {...props}
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  inputMode="decimal"
                  placeholder="1.5"
                />
              )}
            </Field>
            <Field label="Rate per hour ($)">
              {(props) => (
                <Input
                  {...props}
                  value={shownRate}
                  onChange={(e) => setRate(e.target.value)}
                  inputMode="decimal"
                />
              )}
            </Field>
          </div>
          {defaultRateCents === 0 && rate === null ? (
            <p className="text-xs text-foreground-muted">
              No default rate set yet — enter one here, or set it in Settings.
            </p>
          ) : null}
          {can('staff:read') ? (
            <Field label="Technician">
              {(props) => (
                <Select
                  value={staffId ?? assignedStaffId ?? ''}
                  onValueChange={(v) => setStaffId(v)}
                >
                  <SelectTrigger {...props}>
                    <SelectValue placeholder="Me" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeStaff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          ) : null}
          <Field label="Date">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="Note">
            {(props) => (
              <Input
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What was done (optional)"
              />
            )}
          </Field>
          <p className="text-sm font-medium">Amount: {formatMoney(previewCents)}</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid || add.isPending} onClick={() => add.mutate()}>
            Log time
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
